import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

interface TelegramScenario {
  id: string;
  name: string;
  trigger: string;
}

interface TelegramModule {
  id: string;
  name: string;
  type: string;
  description: string | null;
}

interface AssistantModule {
  id?: string;
  moduleId: string;
  order: number;
  config?: any;
  conditions?: any;
  module?: TelegramModule;
}

interface TelegramAssistant {
  id: string;
  name: string;
  description: string | null;
  scenarioId: string;
  config: string | null;
  isActive: boolean;
  scenario?: TelegramScenario;
  modules?: AssistantModule[];
  createdAt: string;
  updatedAt: string;
}

const TelegramAssistants: React.FC = () => {
  const [assistants, setAssistants] = useState<TelegramAssistant[]>([]);
  const [scenarios, setScenarios] = useState<TelegramScenario[]>([]);
  const [modules, setModules] = useState<TelegramModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState<TelegramAssistant | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    scenarioId: '',
    config: '',
    isActive: true
  });
  const [assistantModules, setAssistantModules] = useState<AssistantModule[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assistantsData, scenariosData, modulesData] = await Promise.all([
        apiGet<TelegramAssistant[]>('/telegram-assistants'),
        apiGet<TelegramScenario[]>('/telegram-scenarios'),
        apiGet<TelegramModule[]>('/telegram-modules?isActive=true')
      ]);
      setAssistants(assistantsData);
      setScenarios(scenariosData);
      setModules(modulesData);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (assistant?: TelegramAssistant) => {
    if (assistant) {
      // Загружаем полные данные ассистента
      try {
        const fullAssistant = await apiGet<TelegramAssistant>(`/telegram-assistants/${assistant.id}`);
        setEditingAssistant(fullAssistant);
        setFormData({
          name: fullAssistant.name,
          description: fullAssistant.description || '',
          scenarioId: fullAssistant.scenarioId,
          config: fullAssistant.config ? JSON.stringify(JSON.parse(fullAssistant.config), null, 2) : '',
          isActive: fullAssistant.isActive
        });
        setAssistantModules(
          fullAssistant.modules?.map((m, index) => ({
            moduleId: m.moduleId,
            order: m.order || index + 1,
            config: m.config ? (typeof m.config === 'string' ? JSON.parse(m.config) : m.config) : {},
            conditions: m.conditions ? (typeof m.conditions === 'string' ? JSON.parse(m.conditions) : m.conditions) : null
          })) || []
        );
      } catch (error) {
        console.error('Ошибка загрузки ассистента:', error);
        alert('Ошибка загрузки данных ассистента');
        return;
      }
    } else {
      setEditingAssistant(null);
      setFormData({
        name: '',
        description: '',
        scenarioId: '',
        config: '',
        isActive: true
      });
      setAssistantModules([]);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAssistant(null);
    setFormData({
      name: '',
      description: '',
      scenarioId: '',
      config: '',
      isActive: true
    });
    setAssistantModules([]);
  };

  const handleAddModule = () => {
    const newModule: AssistantModule = {
      moduleId: '',
      order: assistantModules.length + 1,
      config: {},
      conditions: null
    };
    setAssistantModules([...assistantModules, newModule]);
  };

  const handleRemoveModule = (index: number) => {
    const newModules = assistantModules.filter((_, i) => i !== index).map((m, i) => ({
      ...m,
      order: i + 1
    }));
    setAssistantModules(newModules);
  };

  const handleModuleChange = (index: number, field: string, value: any) => {
    const newModules = [...assistantModules];
    newModules[index] = {
      ...newModules[index],
      [field]: value
    };
    setAssistantModules(newModules);
  };

  const handleMoveModule = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === assistantModules.length - 1)
    ) {
      return;
    }

    const newModules = [...assistantModules];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newModules[index], newModules[targetIndex]] = [newModules[targetIndex], newModules[index]];
    
    // Обновляем порядок
    newModules.forEach((m, i) => {
      m.order = i + 1;
    });
    
    setAssistantModules(newModules);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        config: formData.config ? JSON.parse(formData.config) : null,
        modules: assistantModules
          .filter((m) => m.moduleId)
          .map((m) => ({
            moduleId: m.moduleId,
            order: m.order,
            config: m.config || null,
            conditions: m.conditions || null
          }))
      };

      if (editingAssistant) {
        await apiPut(`/telegram-assistants/${editingAssistant.id}`, payload);
      } else {
        await apiPost('/telegram-assistants', payload);
      }

      await fetchData();
      handleCloseModal();
    } catch (error: any) {
      console.error('Ошибка сохранения ассистента:', error);
      alert(error.response?.data?.error || 'Ошибка сохранения ассистента');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить этого ассистента?')) {
      return;
    }

    try {
      await apiDelete(`/telegram-assistants/${id}`);
      await fetchData();
    } catch (error: any) {
      console.error('Ошибка удаления ассистента:', error);
      alert(error.response?.data?.error || 'Ошибка удаления ассистента');
    }
  };

  const getModuleName = (moduleId: string) => {
    const module = modules.find((m) => m.id === moduleId);
    return module?.name || 'Не выбран';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">🤖 Ассистенты Telegram бота</h1>
        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Создать ассистента
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Название
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Описание
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Сценарий
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Модулей
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Статус
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {assistants.map((assistant) => (
              <tr key={assistant.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{assistant.name}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-500">{assistant.description || '-'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{assistant.scenario?.name || '-'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{assistant.modules?.length || 0}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    assistant.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {assistant.isActive ? 'Активен' : 'Неактивен'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleOpenModal(assistant)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    Редактировать
                  </button>
                  <button
                    onClick={() => handleDelete(assistant.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {assistants.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Ассистенты не найдены</p>
          </div>
        )}
      </div>

      {/* Модальное окно конструктора */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingAssistant ? 'Редактировать ассистента' : 'Создать ассистента'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Название *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Сценарий *
                  </label>
                  <select
                    value={formData.scenarioId}
                    onChange={(e) => setFormData({ ...formData, scenarioId: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Выберите сценарий</option>
                    {scenarios.map((scenario) => (
                      <option key={scenario.id} value={scenario.id}>
                        {scenario.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                />
              </div>

              {/* Модули */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Модули (порядок выполнения)
                  </label>
                  <button
                    type="button"
                    onClick={handleAddModule}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    + Добавить модуль
                  </button>
                </div>

                <div className="space-y-3">
                  {assistantModules.map((assistantModule, index) => (
                    <div
                      key={index}
                      className="border border-gray-300 rounded-lg p-4 bg-gray-50"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          Модуль {assistantModule.order}
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleMoveModule(index, 'up')}
                            disabled={index === 0}
                            className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMoveModule(index, 'down')}
                            disabled={index === assistantModules.length - 1}
                            className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveModule(index)}
                            className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                          >
                            Удалить
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Модуль *
                          </label>
                          <select
                            value={assistantModule.moduleId}
                            onChange={(e) => handleModuleChange(index, 'moduleId', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          >
                            <option value="">Выберите модуль</option>
                            {modules.map((module) => (
                              <option key={module.id} value={module.id}>
                                {module.name} ({module.type})
                              </option>
                            ))}
                          </select>
                        </div>

                        {assistantModule.moduleId && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Конфигурация модуля (JSON, опционально)
                            </label>
                            <textarea
                              value={assistantModule.config && Object.keys(assistantModule.config).length > 0 
                                ? JSON.stringify(assistantModule.config, null, 2) 
                                : ''}
                              onChange={(e) => {
                                try {
                                  const parsed = e.target.value.trim() ? JSON.parse(e.target.value) : {};
                                  handleModuleChange(index, 'config', parsed);
                                } catch {
                                  // Игнорируем ошибки парсинга при вводе
                                }
                              }}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                              rows={3}
                              placeholder='{"text": "Переопределение текста"}'
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {assistantModules.length === 0 && (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      Модули не добавлены. Нажмите "Добавить модуль" для начала.
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Конфигурация ассистента (JSON, опционально)
                </label>
                <textarea
                  value={formData.config}
                  onChange={(e) => setFormData({ ...formData, config: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  rows={4}
                  placeholder='{"timeout": 30000, "retry": 3}'
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Активен</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Сохранить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramAssistants;

